import { useMemo } from 'react';

import axios, { type AxiosError } from 'axios';
import { format, isValid } from 'date-fns';

import { useGetExchangeRate } from '@/api/REST/exchangeRate';
import { useGetUserMe } from '@/api/REST/userMe';
import { type CurrencyCodeSelectOption } from '@/api/typesense/currencyCodes';
import { ExpenseFormType } from '@/pages/register/expenses/types';

export const useConvertCurrencies = (
  amount: number,
  originalCurrency: CurrencyCodeSelectOption,
  expenseType: ExpenseFormType,
  date?: Date, // default to today
  targetCurrency?: CurrencyCodeSelectOption, // default to user's currency if not provided
) => {
  const { data: userMeData, isLoading: isLoadingUserMe } = useGetUserMe();

  // this is an ISO code
  const userCurrencyCode = userMeData?.legalEntity?.currencyCode;

  const _targetCurrencyIsoCode = targetCurrency?.isoCode ?? userCurrencyCode;

  // Create a stable date string to avoid infinite re-renders
  const dateString = useMemo(() => {
    if (date && !isValid(date)) {
      console.warn('Invalid date provided to useConvertCurrencies:', date);
      return null;
    }

    const _date = date ?? new Date();

    return format(_date, "yyyy-MM-dd'T'HH:mm:ss"); // to avoid timezone issues do not use toISOString() here
  }, [date]);

  const {
    data: exchangeRateData,
    isLoading: isLoadingExchangeRate,
    isError: isErrorExchangeRate,
    error: exchangeRateError,
  } = useGetExchangeRate(
    originalCurrency?.isoCode,
    _targetCurrencyIsoCode,
    dateString ?? '',
    expenseType,
  );

  if (originalCurrency?.isoCode === _targetCurrencyIsoCode) {
    return { amount: amount, isLoading: false };
  }

  if (isLoadingUserMe || isLoadingExchangeRate) {
    return { amount: null, isLoading: true };
  }

  if (isErrorExchangeRate) {
    // Handle the specific case where exchange rate for the currency is not available (404 with specific message)
    // using legacy error response
    const isExchangeRateForCurrencyNotAvailableError =
      axios.isAxiosError(exchangeRateError) &&
      exchangeRateError?.status === 404 &&
      typeof exchangeRateError?.response?.data === 'string' &&
      (exchangeRateError as AxiosError<string>).response?.data?.startsWith(
        'No exchange rate found',
      );

    if (isExchangeRateForCurrencyNotAvailableError) {
      return {
        amount: null,
        isLoading: false,
        isError: true,
        isExchangeRateForCurrencyNotAvailableError: true,
      };
    }

    return {
      amount: null,
      isLoading: false,
      isError: true,
      error: exchangeRateError,
    };
  }

  if (!exchangeRateData?.rate) {
    return { amount: null, isLoading: false, isError: true };
  }

  return { amount: amount * (exchangeRateData?.rate / 100), isLoading: false };
};
